import { AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure, Icon, useToast, Textarea, Text } from '@chakra-ui/react'
import { useRef } from 'react'
import { Button } from '../../../../../../components/Button'
import { FiAlertTriangle } from 'react-icons/fi'
import { api } from '../../../../../../services/api'
import { useMutation, useQueryClient } from 'react-query'
import { useOrcamentoPedido } from '../../../../../../contexts/OrcamentoPedidoProvider'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTiposPerdidos } from '../../../../../../hooks/useTiposPerdidos'
import { SelectSearch } from '../../../../../../components/SelectSearch'

async function marcarPropostaPerdida({ numeroPedido, motivo, tipo_perdido }) {
    const response = await api.put('/site/v2/orcamento-crm/marcar-perdido', {
        numero_pedido: numeroPedido,
        observacao_perda: motivo,
        tipo_perdido: tipo_perdido,
    })

    return response.data
}

export function MarcarPerdido() {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { infoProposta } = useOrcamentoPedido()
    const { tiposPerdidos, loadingTipoPerdidos } = useTiposPerdidos()
    const cancelRef = useRef()

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm({
        resolver: zodResolver(
            z.object({
                motivo: z.string().nonempty(),
                tipoPerda: z.object({
                    value: z.number().int(),
                    label: z.string().nonempty(),
                }),
            })
        ),
    })

    const queryClient = useQueryClient()

    const toast = useToast({
        position: 'top-right',
        duration: 5000,
        isClosable: true,
    })

    const { mutateAsync, isLoading } = useMutation({
        mutationFn: marcarPropostaPerdida,
        onSuccess: () => {
            queryClient.invalidateQueries('items')

            onClose()

            toast({
                title: 'Proposta marcada como perdida',
                status: 'success',
            })
        },
    })

    async function handleMarcarPerdida(data) {
        await mutateAsync({
            numeroPedido: infoProposta.numero_pedido,
            motivo: data.motivo,
            tipo_perdido: data.tipoPerda.value,
        })
    }

    return (
        <>
            <Button
                onClick={onOpen}
                border={0}
                borderRadius={0}
                bgColor="transparent"
                px={3}
                py={2}
                color="#2D2D2D"
                _hover={{
                    bg: '#e2e2e2',
                }}
                // disabled={infoProposta?.valida_proposta_nova}
                fontWeight="normal"
                leftIcon={<Icon as={FiAlertTriangle} fontSize="20px" color="#FF0000" />}
            >
                Marcar Perdido
            </Button>

            <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent as="form" onSubmit={handleSubmit(handleMarcarPerdida)}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Marcar Proposta como Perdida
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Tem certeza que deseja marcar esta proposta como perdida? Essa ação não poderá ser desfeita.
                            <Text display="block" mt={4} fontSize="sm" fontWeight="bold" as="label" htmlFor="tipoPerda">
                                Motivo da perda
                            </Text>
                            <SelectSearch
                                mt={4}
                                placeholder="Selecione um motivo"
                                isLoading={loadingTipoPerdidos}
                                name="tipoPerda"
                                id="tipoPerda"
                                options={tiposPerdidos?.map((tipo) => ({
                                    value: tipo.tipo_perdido,
                                    label: tipo.descricao,
                                }))}
                                control={control}
                            />
                            <Text display="block" mt={4} fontSize="sm" fontWeight="bold" as="label" htmlFor="descrição">
                                Descrição da perda
                            </Text>
                            <Textarea
                                id="descrição"
                                placeholder="Descrição da perda"
                                resize={'none'}
                                rows={5}
                                borderRadius={0}
                                sx={{
                                    '&::placeholder': {
                                        color: '#929292',
                                    },
                                }}
                                borderColor="#a1a1a1"
                                _focus={{
                                    borderColor: 'black',
                                }}
                                {...register('motivo')}
                            />
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} bgColor="blue.500" onClick={onClose}>
                                Cancelar
                            </Button>
                            <Button colorScheme="red" bgColor="red.500" type="submit" isLoading={isLoading} ml={3}>
                                Confirmar ação
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    )
}
